/**
 * @todo Clean up hours.draft duplication once we have a clear path forward
 */

import type { Range } from 'components/ui-blocks/click-drag-adjust/ClickDragAdjust';
import { EMPTY_ARRAY } from 'constants/defaults';
import { isEqual } from 'lodash-es';
import { createLiveTimestampRange } from 'redux/modules/tic/tic.memories.selectors.utils';
import { createSelector } from 'reselect';
import type { HourAsCard } from 'types/hour';
import { enrichRangesForOverlapping } from 'utils/timeline';
import { isDayLockedForUser } from '~redux/modules/dayProperties';
import { getDayProperties } from '~redux/modules/dayProperties.selectors';
import { getHourCardWithRunningTimer, getHoursAsCards } from './hours';
import {
  type GetHourDraftTimespans,
  getHourDraft,
  getHourDraftAsCard,
  getHourDraftOrCalendarDate,
} from './hours.draft';

/**
 * Get all timestamped `hours` or `draft` for the current day / in context to current TIC Timeline date
 */
export const getTimestampedHoursAndDraftForCurrentDay = createSelector(
  [getHourDraftAsCard, getHoursAsCards, getHourDraftOrCalendarDate],
  (draftAsCard, hourCards, date): Array<HourAsCard> => {
    const allHoursAsCards: Array<HourAsCard> = hourCards.filter(hour => {
      const hasTimestamps = !!hour.timestamps.length || !!hour.from;
      const isCurrentDraft = draftAsCard?.id === hour.id;
      const isThisDay = date === hour.day;
      return hasTimestamps && isThisDay && !isCurrentDraft;
    });

    if (draftAsCard) {
      allHoursAsCards.push(draftAsCard);
    }

    return allHoursAsCards;
  }
);

const getLiveTimestampFromRunningTimer = createSelector(
  [getHourCardWithRunningTimer],
  (liveHourCard): Array<Range> => {
    if (!liveHourCard) return EMPTY_ARRAY;
    return [createLiveTimestampRange(liveHourCard)];
  }
);

export const getAdjustableTimelineTimestamps = createSelector(
  [
    getTimestampedHoursAndDraftForCurrentDay,
    getLiveTimestampFromRunningTimer,
    getHourDraft,
    getDayProperties,
  ],
  (hoursAndDraft, liveTimestamps, hourDraft, dayProperties): GetHourDraftTimespans => {
    const adjustableRanges: Array<Range> = [...liveTimestamps];
    const hourDraftExists = !!hourDraft;

    // Combine all timestamps from hours and draft
    hoursAndDraft.forEach(hourCard => {
      hourCard.timestamps.forEach((timestamp, index) => {
        adjustableRanges.push({
          ...timestamp,
          index,
          hourId: hourCard.id || undefined,
          color: hourCard.projectColor,
          disabled: hourDraftExists && hourDraft?.id !== hourCard.id,
          locked:
            hourCard.isLocked || isDayLockedForUser(hourCard.user_id, dayProperties[hourCard.day]),
          hourCard,
        });
      });
    });

    return {
      hourDraftExists,
      hourDraftId: hourDraft?.id || undefined,
      adjustableRanges: enrichRangesForOverlapping(adjustableRanges),
    };
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);
